/**
 * Полоса прогресса папки эфира в редакторе переводчика (W4.4, фаза 4) — данные и тексты, без React.
 *
 * Источник — translate-python `GET /stream_editor/v1/progress?media=` (переводчику из списка): по папке и языку
 * четыре шага «русский — перевод — голос — в эфире» и «зрители увидят в ЧЧ:ММ» (начало речи папки + задержка
 * зрителей), правки переводчика по папке (заявки полосы правок site_server и их отчёты).
 */
import { fixCueCounts } from "../../../../../utils/pluralRu";

export type StepState =
    | "done"
    | "running"
    | "queued"
    | "failed"
    | "held"
    | "degraded"
    | "updating"
    | "pending";

export interface StreamFix {
    id: string;
    state: string;
    stage?: string;
    kind?: string;
    found?: string;
    /** Что сделал редактор в заявке `text_changed` (site_server `EDITOR_EDITS`): revoice — ♪, text — текст… */
    edit?: string;
    folder?: number;
    lang?: string;
    cue_ids?: string[];
    /** Откуда заявка (`ai` — замена ИИ: вопрос «сокращу сам» она не закрывает, как и в TAG). */
    source?: string;
    /** Реплики, какими они стали после правки (site_server `edit_side_effects.own_cues`): `{новая: прежняя}`. */
    reopened?: Record<string, string> | null;
    /** Куски раскладки: `{фраза: [куски]}`. */
    relaid?: Record<string, string[]> | null;
    text_changed?: string[];
    revoiced?: string[];
    by?: string;
    requested_at?: string;
    settle_until?: string;
    subs_published_at?: string | null;
    published_at?: string | null;
    public_at?: string | null;
    to_record_only?: boolean | null;
    hls_revision?: string | null;
    error?: string | null;
    summary_ru?: string;
    finished_at?: string;
    /**
     * Правка времени (`edit: time`): что человек сделал со временем реплик — {реплика: {from?, to?, want_length?,
     * dub_ms?}} (site_server `edit_timing`). `to` — новое место; `want_length` — растянутая длина озвученного дубля,
     * которую сервер НЕ пишет (её задаёт звук), `dub_ms` — сколько дубль звучит.
     */
    timing?: Record<string, LiveTimingSpec>;
    /**
     * `needs_translator`: вопрос переводчику (site_server `stream_edits`) — правка не влезла и временем (п.50г) или
     * правка времени увела реплику дальше предела от слова лектора (`cause.kind: edit_drift`). Кнопки ответа — ровно
     * `question.answers`.
     */
    question?: FixQuestion | null;
    /** Ответ на вопрос: кнопкой или сервером (`resolved` — новая правка той же реплики прошла, `probe_retired`). */
    answered?: FixAnswered | null;
    restored?: boolean;
    /**
     * Правка пришла во время прохода своей папки и ждёт его конца — номер папки (site_server `with_pass_wait`,
     * проба P50m10e: ♪ ждал 114 с, а тег, тост и шапка говорили «в работе»). Круг начнётся сразу после прохода.
     */
    waits_folder_pass?: number | null;
}

export interface FixQuestion {
    text_ru?: string;
    short_ms?: number | null;
    neighbors?: { id: string; text?: string | null; shorter_ms?: number | null }[];
    /** Текст правки, который не влез: `{реплика: текст}` (папка откатана — на ленте его уже нет). */
    proposed?: Record<string, string> | null;
    /** Реплики самой правки с перебором (их сократить может только переводчик). */
    own?: { id: string; text?: string | null }[] | null;
    answers?: string[];
    restored?: boolean;
    on_air_ru?: string;
    cause?: { kind?: string | null; cue_id?: string | null; ms?: number | null; limit_ms?: number | null } | null;
}

export interface FixAnswered {
    answer?: string;
    /** У `resolved`: `time` — новое место, `edit` — правка текста, `keep` — «оставить как есть», `in_limit`. */
    how?: string | null;
    fix_id?: string | null;
    by?: string | null;
    at?: string;
    auto?: boolean;
}

export interface LiveTimingSpec {
    from?: number | null;
    to?: number | null;
    want_length?: number | null;
    dub_ms?: number | null;
    /** Длина НЕозвученной реплики, которую человек растянул: записана в папку, в эфир не уходит (звука нет). */
    length?: { from?: number | null; to?: number | null } | null;
}

/** Правдивая половина тоста о длине неозвученной реплики (03.11). */
export const UNVOICED_LENGTH_ON_DISK = "длина неозвученной реплики записана в папку — звука у неё нет, в эфир она не уходит";

/** Правдивая половина тоста о растянутом дубле (проба P50m10d, 03.11). */
export const DUB_LENGTH_IS_ITS_SOUND = "длину дубля задаёт его звук; сжать или растянуть дубль — темп ♪";

export interface ProgressLang {
    stage?: string;
    state?: string;
    degraded?: boolean;
    published_at?: string | null;
    steps: Record<string, StepState> & { current?: string; title?: string };
    fixes: StreamFix[];
    fixes_open: number;
    /** Из открытых — сколько ждут конца прохода папки (translate-python `fixes_wait_pass`). */
    fixes_wait_pass?: number;
}

export interface ProgressFolder {
    folder: number;
    closed_at?: string | null;
    air_start_at?: string | null;
    public_at?: string | null;
    langs: Record<string, ProgressLang>;
    ru_sync?: { status?: string; mode?: string; languages?: Record<string, { status?: string; error?: string }> };
}

export interface StreamProgressData {
    media: string;
    open?: boolean;
    closed?: boolean;
    strict?: boolean;
    held?: boolean;
    held_folders?: number[];
    viewer_delay_sec?: number | null;
    steps: { id: string; title: string }[];
    folders: ProgressFolder[];
    fixes_error?: string;
}

export const STEP_TITLES: Record<string, string> = {
    ru: "русский",
    translate: "перевод",
    voice: "голос",
    air: "в эфире",
};

export const STATE_MARK: Record<StepState, string> = {
    done: "✓",
    running: "⟳",
    queued: "…",
    failed: "✕",
    held: "⏸",
    degraded: "!",
    updating: "⟳",
    pending: "·",
};

export const STATE_COLOR: Record<StepState, string> = {
    done: "green",
    running: "processing",
    queued: "default",
    failed: "error",
    held: "warning",
    degraded: "warning",
    updating: "processing",
    pending: "default",
};

export const OPEN_FIX_STATES = ["queued", "settling", "running"];
/**
 * `needs_translator` (п.50г, 02.11): правка не влезла и временем, а соседние реплики без согласия переводчика не
 * сокращаются — круг откатил папку (в эфире прежняя версия) и спрашивает: сократить свой текст или разрешить сократить
 * соседнюю. Правка времени дальше предела от слова лектора (03.11) — так же: вопрос, в эфире прежнее место; ответ —
 * новое место или «Оставить как в эфире». Кнопки — ровно `question.answers` (тост вопроса и «Эфир: папки и правки»).
 */
export const ASK_FIX_STATE = "needs_translator";
export const FINAL_FIX_STATES = ["fixed", "failed", "nothing_to_fix", "refused", ASK_FIX_STATE];

/** Язык пары в ответе: редактор зовёт китайский «ch», эфир — «zh» (и наоборот). */
export const langOf = (folder: ProgressFolder | undefined, lang: string): ProgressLang | undefined => {
    if (!folder) return undefined;
    const aliases = lang === "ch" ? ["ch", "zh"] : lang === "zh" ? ["zh", "ch"] : [lang];
    for (const name of aliases) {
        if (folder.langs?.[name]) return folder.langs[name];
    }
    return undefined;
};

/** ЧЧ:ММ местного времени. */
export const hhmm = (iso?: string | null): string | null => {
    if (!iso) return null;
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
};

/** «зрители увидят в ЧЧ:ММ» / «зрители уже видят (с ЧЧ:ММ)». */
export const viewersLine = (publicAt?: string | null, now: number = Date.now()): string | null => {
    const at = hhmm(publicAt);
    if (!at || !publicAt) return null;
    return new Date(publicAt).getTime() > now ? `зрители увидят в ${at}` : `зрители видят с ${at}`;
};

const ISO_TIME = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?/g;

/** Время отчёта сервера (ISO) — ЧЧ:ММ местного времени: «в эфире: 18:20; зрители увидят: 18:40». */
export const localTimes = (text: string): string => text.replace(ISO_TIME, (iso) => hhmm(iso) || iso);

/** Папка, конца чьего прохода ждёт правка (`waits_folder_pass`); null — не ждёт (или полоса старше поля). */
export const passWaitOf = (fix: { waits_folder_pass?: number | null } | null | undefined): number | null => {
    const n = fix?.waits_folder_pass;
    return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
};

/** «ждёт конца прохода папки 1 — круг правки начнётся сразу после него» — тост, тег и шапка говорят одно. */
export const passWaitLine = (folder: number): string =>
    `ждёт конца прохода папки ${folder} — круг правки начнётся сразу после него`;

/** Тег правки, что ждёт прохода папки, — коротко. */
export const passWaitTag = (folder: number): string => `ждёт прохода папки ${folder}`;

/** Счётчик шапки: «правок в работе: 2» или, пока они ждут прохода папки, — так и сказано. */
export const openFixesLine = (open: number, waiting: number, folder: number): string => {
    if (open <= 0) return "";
    if (waiting <= 0) return `правок в работе: ${open}`;
    if (waiting >= open) return `правок в работе: ${open} — ждут конца прохода папки ${folder}`;
    return `правок в работе: ${open}, из них ждут конца прохода папки ${folder}: ${waiting}`;
};

/**
 * Тост «Правка принята»: что уйдёт в эфир. У ♪ — новый дубль, а не «перевод и голос» (проба P50m10d, E6: ♪ — это
 * переозвучка, текст не менялся).
 */
const acceptedWhat = (fix: StreamFix): string => {
    const what = fix.found || fix.kind || "правка";
    switch (fix.edit) {
        case "revoice":
            return `${what}: уйдёт в эфир сама — новый дубль реплики, новая ревизия`;
        case "skip":
        case "unskip":
        case "delete_audio":
            return `${what}: уйдёт в эфир сама — звук папки без пересинтеза, новая ревизия`;
        case "time": {
            // Правка времени (liveTiming.ts, проба P50m10d): соседей подгоняет временем и сверяет с лектором сервер.
            // Растянутый озвученный дубль (`want_length`) в эфир не уходит — его длину задаёт звук (03.11): тост
            // говорит это, а не «новое место реплики».
            const specs = Object.values(fix.timing || {});
            const moved = specs.some((s) => typeof s?.to === "number");
            const stretched = specs.some((s) => typeof s?.want_length === "number");
            const unvoiced = specs.some((s) => typeof s?.length?.to === "number");
            const place = `${what}: уйдёт в эфир сама — новое место реплики, соседей подгонит временем круг правки, новая ревизия`;
            const notes = [
                ...(stretched ? [DUB_LENGTH_IS_ITS_SOUND] : []),
                ...(unvoiced ? [UNVOICED_LENGTH_ON_DISK] : []),
            ];
            if (!moved && notes.length) return `${what}: в эфир ничего не уходит — ${notes.join("; ")}`;
            return notes.length ? `${place}; ${notes.join("; ")}` : place;
        }
        default:
            return `${what}: уйдёт в эфир сама — перевод и голос правленых реплик, новая ревизия`;
    }
};

export const acceptedLine = (fix: StreamFix): string => {
    const line = acceptedWhat(fix);
    const wait = passWaitOf(fix);
    // Пришла во время прохода своей папки: круг правки начнётся после него — тост говорит это сразу (P50m10e).
    return wait ? `${line}; сейчас ${passWaitLine(wait)}` : line;
};

/** Отчёт правки одной строкой для тоста. */
export const fixLine = (fix: StreamFix): string => {
    // Отчёт сервера, написанный до исправления склонения, — «(1 реплик)» склоняется здесь (P50m10).
    if (fix.summary_ru) return localTimes(fixCueCounts(fix.summary_ru));
    const what = fix.found || fix.kind || "правка";
    if (fix.state === "fixed") {
        const when = fix.to_record_only
            ? "уже показанная часть — исправлено в записи"
            : viewersLine(fix.public_at) || "в эфире";
        return `${what}: в эфире ${hhmm(fix.published_at || fix.subs_published_at) || ""} — ${when}`.trim();
    }
    if (fix.state === "refused" || fix.state === "failed") return `${what}: ${fix.error || fix.state}`;
    if (fix.state === "nothing_to_fix") return `${what}: менять нечего`;
    return `${what}: ${fix.stage || fix.state}`;
};

export interface FixEvent {
    /**
     * `closed` — вопрос заявки закрыт так, как его считает TAG (`questionState`): ответ кнопкой «оставить как в
     * эфире», новая правка ответа закончилась, сервер закрыл сам. Открытый вопрос (ждёт ответа или ответ принят, а
     * новая правка ещё идёт) — `finished`: его тост с текстом, который не влез, показывается или обновляется.
     */
    kind: "accepted" | "finished" | "closed";
    fix: StreamFix;
}

/**
 * Что помнить о заявке между опросами: состояние, а у вопроса — и его ход (ждёт ответа / ответ принят — и какая новая
 * правка его держит / закрыт): тост вопроса обновляется на каждом шаге хода.
 */
const seenKey = (fix: StreamFix, fixes: StreamFix[]): string => {
    if (fix.state !== ASK_FIX_STATE) return fix.state;
    const q = questionState(fix, fixes);
    return `${fix.state}:${q.phase}${q.phase === "answered" && q.follow ? `:${q.follow.id}` : ""}`;
};

/**
 * Что сказать тостом: новые заявки этого переводчика («принята») и те, что дошли до итога. `seen` — что уже
 * сказано (id → состояние), меняется на месте. Первый проход (`seen` пуст) только запоминает: старые правки
 * при открытии редактора не всплывают — кроме ОТКРЫТОГО вопроса своей правки: после перезагрузки его тост с кнопками
 * и текстом, который не влез, нужен снова (иначе вопрос старше пяти заявок оставался без кнопок, 04.11), — и
 * ЗАКРЫТОГО вопроса, чей текст, который не влез, так и не лёг (`proposedPending`): его тост закрытия висит, пока
 * человек его не закрыл (`dismissed` — какие он закрыл), иначе после перезагрузки текст пропадал с экрана (04.11).
 */
export const fixEvents = (
    fixes: StreamFix[],
    seen: Map<string, string>,
    me: string | null,
    firstPass: boolean,
    dismissed: ReadonlySet<string> = new Set<string>(),
): FixEvent[] => {
    const out: FixEvent[] = [];
    for (const fix of fixes) {
        if (!fix?.id) continue;
        if (me && fix.by && fix.by.toLowerCase() !== me.toLowerCase()) continue;
        const before = seen.get(fix.id);
        const now = seenKey(fix, fixes);
        seen.set(fix.id, now);
        if (before === now) continue;
        const phase = questionState(fix, fixes).phase;
        if (firstPass) {
            if (phase === "asking" || phase === "answered") out.push({ kind: "finished", fix });
            else if (phase === "closed" && !dismissed.has(fix.id) && proposedPending(fix, fixes)) {
                out.push({ kind: "closed", fix });
            }
            continue;
        }
        if (phase === "closed") {
            out.push({ kind: "closed", fix });
        } else if (before === undefined) {
            out.push({ kind: FINAL_FIX_STATES.includes(fix.state) ? "finished" : "accepted", fix });
        } else if (FINAL_FIX_STATES.includes(fix.state)) {
            out.push({ kind: "finished", fix });
        }
    }
    return out;
};

/** Чьё: заявки этого переводчика (без адреса — все). */
export const isMine = (fix: StreamFix, me: string | null): boolean =>
    !me || !fix.by || fix.by.toLowerCase() === me.toLowerCase();

/** Правки этого переводчика по папке и языку, что ещё идут (их ход видно после «Готово — в эфир»). */
export const openOwnFixIds = (fixes: StreamFix[], me: string | null): string[] =>
    fixes.filter((fix) => fix?.id && isMine(fix, me) && OPEN_FIX_STATES.includes(fix.state)).map((fix) => fix.id);

export type OwnFixPhase = "none" | "accepted" | "air" | "record" | "nothing" | "failed" | "ask" | "answered" | "closed";

export interface OwnFixStatus {
    phase: OwnFixPhase;
    /** Коротко, рядом с кнопкой. */
    text: string;
    /** Подсказка (title). */
    title: string;
}

/**
 * Ход своей правки после «Готово — в эфир»: по заявкам, что ушли этим нажатием (`ids`), и опросу прогресса.
 * «принята» → «в эфире · зрители увидят ЧЧ:ММ»; по-русски, без машинных fixed/refused.
 */
export const ownFixStatus = (fixes: StreamFix[], ids: string[], now: number = Date.now()): OwnFixStatus => {
    if (!ids.length) {
        return { phase: "none", text: "правок нет", title: "Ждущих правок нет — в эфире всё как есть" };
    }
    const byId = new Map(fixes.filter((fix) => fix?.id).map((fix) => [fix.id, fix]));
    const mine = ids.map((id) => byId.get(id));
    if (mine.some((fix) => !fix || !FINAL_FIX_STATES.includes(fix.state))) {
        return {
            phase: "accepted",
            text: "принята",
            title: "Правка принята: перевод и голос правленых реплик уйдут в эфир сами",
        };
    }
    const done = mine as StreamFix[];
    const phaseOf = (fix: StreamFix) => questionState(fix, fixes).phase;
    const asked = done.find((fix) => phaseOf(fix) === "asking");
    if (asked) return { phase: "ask", text: askShort(asked), title: fixLine(asked) };
    // Ответ принят, а вопрос открыт (новая правка ответа ещё идёт или короткой правки ещё нет) — не серым «закрыт».
    const answered = done.find((fix) => phaseOf(fix) === "answered");
    if (answered) {
        return { phase: "answered", text: answeredShort(answered, fixes), title: answeredLine(answered, fixes) };
    }
    const bad = done.find((fix) => fix.state === "failed" || fix.state === "refused");
    if (bad) return { phase: "failed", text: "✕ ошибка: не вышла в эфир", title: fixLine(bad) };
    const fixed = done.filter((fix) => fix.state === "fixed");
    const closed = done.find((fix) => phaseOf(fix) === "closed");
    if (!fixed.length && closed) {
        return { phase: "closed", text: closedShort(closedLine(closed, fixes)), title: fixLine(closed) };
    }
    if (!fixed.length) return { phase: "nothing", text: "менять нечего", title: fixLine(done[0]) };
    if (fixed.every((fix) => fix.to_record_only)) {
        return { phase: "record", text: "✓ исправлено в записи", title: "Эту часть зрители уже видели — исправлено в записи" };
    }
    const publicAt = fixed
        .map((fix) => fix.public_at)
        .filter((at): at is string => Boolean(at) && !Number.isNaN(new Date(at as string).getTime()))
        .sort()
        .pop();
    const viewers = viewersLine(publicAt, now);
    return {
        phase: "air",
        text: viewers ? `✓ в эфире · ${viewers}` : "✓ в эфире",
        title: fixed.map(fixLine).join("\n"),
    };
};

// ── Вопрос переводчику: кнопки ответа и закрытый вопрос (как в плеере site_eng, `streamFixReport`) ─────────────────

/** Ответ кнопкой (translate-python `POST /stream_editor/v1/fixes/{id}/answer` → site_server `…/fixes/{id}/answer`). */
export type FixAnswer = "shorten_own" | "allow_neighbor" | "keep_on_air";

/** Кнопки, которые знает редактор; чего он не знает — кнопкой не рисуется. */
export const FIX_ANSWER_BUTTONS: FixAnswer[] = ["shorten_own", "allow_neighbor", "keep_on_air"];

/** Ответы, которые закрывают вопрос сразу (TAG `translator_question.CLOSING_ANSWERS`). */
export const CLOSING_ANSWERS = ["keep_on_air", "resolved", "probe_retired"];

/** Предел отхода реплики от слова лектора у правки времени (site_server `edit_timing.MAX_DRIFT_MS`), если вопрос молчит. */
const DRIFT_LIMIT_MS = 2000;

/**
 * Ход вопроса правки — ровно как его считает TAG (`translator_question._status`, его правило держит папку: пока вопрос
 * открыт, склейка не сокращает и не удлиняет текст соседей правки, доска и «Доделать» пишут «у папки открыт вопрос
 * переводчику»):
 *  * `asking` — ответа нет;
 *  * `closed` — «оставить как в эфире», `resolved` (новая правка той же реплики прошла, реплика снова в пределе),
 *    `probe_retired`; «разрешить сократить соседнюю» — когда новая заявка ответа (`answered.fix_id`) закончилась;
 *    «сокращу сам» — когда закончилась новая правка человека (не ИИ) тех же реплик той же папки и языка, поданная не
 *    раньше ответа (допуск 1 с, как в TAG);
 *  * `answered` — ответ принят, а вопрос ещё открыт: новая правка ответа идёт (`follow`) или её ещё нет; ответ, которого
 *    редактор не знает, — тоже открыт (как в TAG).
 * `fixes` — заявки, среди которых искать новую правку (полоса: все папки и языки — сравниваются только той же папки и
 * языка). `follow` — новая правка, которая держит или закрыла вопрос.
 */
export type QuestionPhase = "none" | "asking" | "answered" | "closed";

export interface QuestionState {
    phase: QuestionPhase;
    follow?: StreamFix | null;
}

const LANG_ALIAS: Record<string, string> = { ch: "zh" };
const sameLang = (a?: string, b?: string): boolean => {
    const x = String(a || "").trim().toLowerCase();
    const y = String(b || "").trim().toLowerCase();
    return (LANG_ALIAS[x] || x) === (LANG_ALIAS[y] || y);
};

const sameFolderLang = (a: StreamFix, b: StreamFix): boolean =>
    Number(a.folder || 0) === Number(b.folder || 0) && sameLang(a.lang, b.lang);

/** Тот же человек (кого нет — не спорим: считаем тем же). */
const sameBy = (a?: string | null, b?: string | null): boolean => {
    const x = String(a || "").trim().toLowerCase();
    const y = String(b || "").trim().toLowerCase();
    return !x || !y || x === y;
};

/** ISO → секунды (как TAG `_iso`): микросекунды отрезаются до мс, нет времени — 0. */
const isoSeconds = (value?: string | null): number => {
    const raw = String(value || "").trim();
    if (!raw) return 0;
    const ms = Date.parse(raw.replace(/(\.\d{3})\d+/, "$1"));
    return Number.isFinite(ms) ? ms / 1000 : 0;
};

/** Реплики правки — какими они были и какими стали (TAG `translator_question.edit_cues`), и реплики её вопроса. */
export const editCues = (fix: StreamFix): Set<string> => {
    const out = new Set<string>();
    const add = (value: unknown) => {
        if (value !== null && value !== undefined && String(value)) out.add(String(value));
    };
    (fix.cue_ids || []).forEach(add);
    Object.entries(fix.reopened || {}).forEach(([cue, parent]) => {
        add(cue);
        add(parent);
    });
    Object.entries(fix.relaid || {}).forEach(([parent, pieces]) => {
        add(parent);
        (pieces || []).forEach(add);
    });
    (fix.revoiced || []).forEach(add);
    (fix.text_changed || []).forEach(add);
    Object.keys(fix.question?.proposed || {}).forEach(add);
    (fix.question?.own || []).forEach((item) => add(item?.id));
    return out;
};

export const questionState = (fix: StreamFix, fixes: StreamFix[] = []): QuestionState => {
    if (fix.state !== ASK_FIX_STATE) return { phase: "none" };
    const answered = fix.answered;
    if (!answered) return { phase: "asking" };
    const answer = String(answered.answer || "");
    if (CLOSING_ANSWERS.includes(answer)) {
        const id = String(answered.fix_id || "");
        return { phase: "closed", follow: (id && fixes.find((one) => one.id === id)) || null };
    }
    if (answer === "allow_neighbor") {
        const id = String(answered.fix_id || "");
        const follow = id ? fixes.find((one) => one.id === id) : undefined;
        if (follow && !OPEN_FIX_STATES.includes(follow.state)) return { phase: "closed", follow };
        return { phase: "answered", follow: follow || null };
    }
    if (answer === "shorten_own") {
        const at = isoSeconds(answered.at);
        const cues = editCues(fix);
        let running: StreamFix | null = null;
        for (const other of fixes) {
            if (!other?.id || other.id === fix.id || String(other.source || "") === "ai" || !sameFolderLang(other, fix)) {
                continue;
            }
            if (isoSeconds(other.requested_at) + 1 < at) continue;
            if (!Array.from(editCues(other)).some((cue) => cues.has(cue))) continue;
            if (OPEN_FIX_STATES.includes(other.state)) {
                if (!running) running = other;
                continue;
            }
            return { phase: "closed", follow: other };
        }
        return { phase: "answered", follow: running };
    }
    return { phase: "answered", follow: null };
};

/** Вопрос ждёт ответа. Полоса старше поля `question` — по состоянию заявки. */
export const isAsking = (fix: StreamFix): boolean => fix.state === ASK_FIX_STATE && !fix.answered;

/** Вопрос открыт по правилу TAG: ждёт ответа или ответ принят, а новая правка ещё не закончилась. */
export const isQuestionOpen = (fix: StreamFix, fixes: StreamFix[] = []): boolean => {
    const phase = questionState(fix, fixes).phase;
    return phase === "asking" || phase === "answered";
};

/** Вопрос закрыт по правилу TAG (см. `questionState`). */
export const isQuestionClosed = (fix: StreamFix, fixes: StreamFix[] = []): boolean =>
    questionState(fix, fixes).phase === "closed";

/** Вопрос правки времени: реплика встала бы дальше предела от слова лектора. */
export const isDriftQuestion = (fix: StreamFix): boolean => fix.question?.cause?.kind === "edit_drift";

const isRepeatQuestion = (fix: StreamFix): boolean => fix.question?.cause?.kind === "repeat_neighbor";

/** Кнопки ответа на открытый вопрос — ровно `question.answers` сервера, в его порядке; закрыт или вопроса нет — пусто. */
export const questionAnswers = (fix: StreamFix): FixAnswer[] => {
    if (!isAsking(fix)) return [];
    const raw = fix.question?.answers;
    if (!Array.isArray(raw)) return [];
    const out: FixAnswer[] = [];
    for (const one of raw) {
        const answer = String(one) as FixAnswer;
        if (FIX_ANSWER_BUTTONS.includes(answer) && !out.includes(answer)) out.push(answer);
    }
    return out;
};

/** Секунды с сотыми: 2000 мс → «2,00». */
const seconds2 = (ms: number): string => {
    const cs = Math.floor((Math.round(ms) + 5) / 10);
    return `${Math.floor(cs / 100)},${String(cs % 100).padStart(2, "0")}`;
};

const driftLimitRu = (fix: StreamFix): string => {
    const ms = fix.question?.cause?.limit_ms;
    return `${seconds2(typeof ms === "number" && ms > 0 ? ms : DRIFT_LIMIT_MS)} с`;
};

/** Надпись кнопки ответа. */
export const answerTitle = (answer: FixAnswer, fix: StreamFix): string => {
    const many = (fix.question?.neighbors || []).length > 1;
    const repeat = isRepeatQuestion(fix);
    if (answer === "keep_on_air") return "Оставить как в эфире";
    if (answer === "shorten_own") return repeat ? "Исправить свой текст" : "Сократить свой текст";
    if (answer === "allow_neighbor") return `Разрешить ${repeat ? "снять" : "сократить"} ${many ? "соседние" : "соседнюю"}`;
    return String(answer);
};

/** Под кнопками открытого вопроса: что сейчас в эфире и как ещё на него ответить. */
export const questionNote = (fix: StreamFix): string => {
    if (isDriftQuestion(fix)) {
        const keep = questionAnswers(fix).includes("keep_on_air");
        return (
            "Ваше время не выложено, эфир идёт. Поставьте реплику ближе к слову лектора здесь, на ленте, — новая " +
            "правка уйдёт сама и закроет вопрос" +
            (keep
                ? "; или оставьте как в эфире — ничего не изменится."
                : ". Кнопки «Оставить как в эфире» нет: папку не удалось вернуть к тому, что в эфире.")
        );
    }
    const verb = isRepeatQuestion(fix) ? "не снимаются" : "не сокращаются";
    return `Соседние реплики без вашего согласия ${verb}. Пока нет ответа, в эфире — прежняя версия папки.`;
};

/** Текст правки, который не влез (папка откатана — на ленте его уже нет): показать, чтобы он не пропал молча. */
export const proposedTexts = (fix: StreamFix): string[] =>
    Object.values(fix.question?.proposed || {})
        .map((text) => String(text || "").trim())
        .filter(Boolean);

/** ЧЧ:ММ подачи новой правки, если она известна: « (21:12)». */
const atOf = (follow?: StreamFix | null): string => {
    const at = hhmm(follow?.requested_at);
    return at ? ` (${at})` : "";
};

/**
 * Новая правка «сокращу сам» — чья: своя — «ваша новая правка (21:12)»; второго переводчика того же круга (реплика из
 * `question.own`, TAG закрывает и ею) — «новая правка другого переводчика (адрес, 21:12)», а не «ваша».
 */
const ownFollowWord = (fix: StreamFix, follow?: StreamFix | null): string => {
    if (follow && !sameBy(follow.by, fix.by)) {
        const at = hhmm(follow.requested_at);
        return `новая правка другого переводчика (${follow.by}${at ? `, ${at}` : ""})`;
    }
    return `ваша новая правка${atOf(follow)}`;
};

/** Чем закончилась новая правка, закрывшая вопрос: «в эфире 21:14», «менять нечего», «не вышла в эфир: …». */
const followOutcome = (follow?: StreamFix | null): string => {
    if (!follow) return "";
    switch (follow.state) {
        case "fixed": {
            const at = hhmm(follow.published_at || follow.subs_published_at);
            return follow.to_record_only ? "исправлено в записи" : `в эфире${at ? ` ${at}` : ""}`;
        }
        case "nothing_to_fix":
            return "менять нечего";
        case ASK_FIX_STATE:
            return "не влезла и она — у неё свой вопрос вам";
        case "failed":
        case "refused":
            return `не вышла в эфир${follow.error ? `: ${follow.error}` : ""}`;
        default:
            return follow.state;
    }
};

const neighbourWord = (fix: StreamFix): string =>
    `${isRepeatQuestion(fix) ? "снять" : "сократить"} ${(fix.question?.neighbors || []).length > 1 ? "соседние" : "соседнюю"}`;

/**
 * Ответ принят, а вопрос открыт (TAG держит его, пока новая правка не закончилась): что сейчас. Вопрос ждёт ответа
 * или закрыт — "".
 */
export const answeredLine = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "answered") return "";
    const answer = String(fix.answered?.answer || "");
    if (answer === "allow_neighbor") {
        return state.follow || fix.answered?.fix_id
            ? `Ответ принят: вы разрешили ${neighbourWord(fix)} — идёт новая правка${atOf(state.follow)}; вопрос открыт, ` +
                  "пока она не закончится"
            : `Ответ принят: вы разрешили ${neighbourWord(fix)} — новой правки сервер не поставил; вопрос открыт`;
    }
    if (answer === "shorten_own") {
        return state.follow
            ? `Ответ принят: вы сокращаете свой текст — идёт ${ownFollowWord(fix, state.follow)}` +
                  `${sameBy(state.follow.by, fix.by) ? "" : " тех же реплик"}; вопрос открыт, пока она не закончится`
            : "Ждём вашу короткую правку: сократите текст реплики здесь, на ленте, и сохраните — новая правка уйдёт в " +
                  "эфир сама; вопрос открыт, пока она не закончится";
    }
    return `Ответ «${answer || "?"}» принят — какой, редактор не знает; вопрос считается открытым`;
};

/** Коротко (рядом с «Готово — в эфир»): «ответ принят: идёт новая правка», «ждём вашу короткую правку». */
export const answeredShort = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "answered") return "";
    if (String(fix.answered?.answer || "") === "shorten_own" && !state.follow) return "ждём вашу короткую правку";
    return `ответ принят: идёт новая правка${atOf(state.follow)}`;
};

/**
 * Закрытый вопрос словами: «Вопрос закрыт: …». Новая правка, закрывшая вопрос, — из `fixes` (её время и итог).
 * Вопрос открыт — "".
 */
export const closedLine = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "closed") return "";
    const answered: FixAnswered = fix.answered || {};
    const answer = String(answered.answer || "");
    const newFix = `новая правка${atOf(state.follow)}`;
    const outcome = followOutcome(state.follow);
    switch (answer) {
        case "keep_on_air":
            return "Вопрос закрыт: оставлено как в эфире — место реплики прежнее, ничего не менялось";
        case "probe_retired":
            return "Вопрос закрыт: проба снята — круг правки не запускался, в эфир ничего не ушло";
        case "allow_neighbor":
            return `Вопрос закрыт: ${newFix} с разрешением ${neighbourWord(fix)} закончилась${outcome ? ` — ${outcome}` : ""}`;
        case "shorten_own":
            return `Вопрос закрыт: ${ownFollowWord(fix, state.follow)} тех же реплик закончилась${outcome ? ` — ${outcome}` : ""}`;
        case "resolved": {
            const how = String(answered.how || "");
            if (how === "time") {
                return `Вопрос закрыт: реплику поставила ${newFix} — её место выложено и сверено с выравниванием`;
            }
            if (how === "keep") return `Вопрос закрыт: ${newFix} оставила реплику как есть`;
            if (how === "in_limit") return `Вопрос закрыт: реплика снова у слова лектора (в пределе ${driftLimitRu(fix)})`;
            return `Вопрос закрыт: реплику выложила ${newFix}`;
        }
        default:
            return `Вопрос закрыт (ответ «${answer || "?"}»)`;
    }
};

/** Коротко (рядом с «Готово — в эфир»): «вопрос закрыт: оставлено как в эфире». */
export const closedShort = (line: string): string => line.replace(/^Вопрос закрыт/, "вопрос закрыт").split(" — ")[0];

/**
 * Закрытый вопрос с текстом, который не влез (и после «оставить как в эфире», и после новой правки): этот текст
 * переводчика в папке так и не лёг — он виден, с подсказкой, что его можно вписать снова (п.50г: текст переводчика не
 * пропадает молча, и с экрана).
 */
export const PROPOSED_AGAIN_HINT = "его можно вписать снова: исправьте реплику здесь, на ленте, и сохраните — новая правка уйдёт в эфир сама";

/** Коротко об открытом вопросе (рядом с «Готово — в эфир»): что ответить — по кнопкам вопроса. */
export const askShort = (fix: StreamFix): string => {
    if (!fix.question) return "⚠ не влезает — сократите текст или разрешите сократить соседнюю (плеер, «Мои правки»)";
    const titles = questionAnswers(fix).map((answer) => `«${answerTitle(answer, fix)}»`);
    if (isDriftQuestion(fix)) {
        return `⚠ время не выложено — поставьте реплику ближе к слову лектора${titles.length ? ` или ${titles.join(" или ")}` : ""}`;
    }
    return `⚠ не влезает — ${titles.length ? titles.join(" или ") : "сократите свой текст"}`;
};

/** Ответ кнопкой принят, а опрос ещё не принёс его: что теперь (вопрос открыт, пока новая правка не закончится). */
export const answeredHere = (answer: FixAnswer, fix: StreamFix): string => {
    if (answer === "keep_on_air") {
        return "Вопрос закрыт: оставлено как в эфире — место реплики прежнее, ничего не менялось";
    }
    if (answer === "allow_neighbor") {
        return (
            `Ответ принят: новая правка того же текста ${isRepeatQuestion(fix) ? "снимет повтор у соседней" : "сократит соседнюю"} ` +
            "— её ход в «Эфир: папки и правки»; вопрос открыт, пока она не закончится"
        );
    }
    return (
        "Ответ принят — ждём вашу короткую правку: сократите текст реплики здесь, на ленте, и сохраните — новая правка " +
        "уйдёт в эфир сама; вопрос открыт, пока она не закончится"
    );
};

/** Правки, которые текст реплики не пишут: время, ♪, снятие с озвучки; вид «голос» и «оставить как есть». */
const NOT_TEXT_EDITS = ["time", "revoice", "skip", "unskip", "delete_audio"];
const writesText = (fix: StreamFix): boolean => {
    const kind = String(fix.kind || "");
    return !NOT_TEXT_EDITS.includes(String(fix.edit || "")) && !kind.startsWith("vo.") && kind !== "keep";
};

/**
 * Текст, который не влез, ушёл в новую правку: «разрешить сократить соседнюю» — тот же текст ушёл новой заявкой ответа,
 * и она закончилась; иначе — тот же переводчик потом переписал текст этих реплик (новая правка текста той же папки и
 * языка, не ИИ, поданная позже вопроса и не раньше ответа, закончилась; не влезла и она — её текст на экране её
 * вопросом). После «оставить как в эфире» без новой правки — не ушёл.
 */
export const proposedCarried = (fix: StreamFix, fixes: StreamFix[] = []): boolean => {
    if (!proposedTexts(fix).length) return true;
    const state = questionState(fix, fixes);
    if (String(fix.answered?.answer || "") === "allow_neighbor" && state.phase === "closed" && state.follow) return true;
    const keys = Object.keys(fix.question?.proposed || {});
    const since = isoSeconds(fix.answered?.at || fix.finished_at || fix.requested_at);
    return fixes.some(
        (other) =>
            Boolean(other?.id) &&
            other.id !== fix.id &&
            String(other.source || "") !== "ai" &&
            sameFolderLang(other, fix) &&
            sameBy(other.by, fix.by) &&
            !OPEN_FIX_STATES.includes(other.state) &&
            isoSeconds(other.requested_at) > isoSeconds(fix.requested_at) &&
            isoSeconds(other.requested_at) + 1 >= since &&
            writesText(other) &&
            Array.from(editCues(other)).some((cue) => keys.includes(cue)),
    );
};

/**
 * Вопрос закрыт, а текст переводчика, который не влез, так и не лёг и в новую правку не ушёл (п.50г: текст не
 * пропадает молча, и с экрана): его держат видимым — тостом и в окне «папки и правки», — пока человек не закрыл тост.
 */
export const proposedPending = (fix: StreamFix, fixes: StreamFix[] = []): boolean =>
    questionState(fix, fixes).phase === "closed" && proposedTexts(fix).length > 0 && !proposedCarried(fix, fixes);

/** Сколько последних правок папки видно в окне «папки и правки»; открытые вопросы видны всегда, сверх этого. */
export const SHOWN_FIXES = 5;

/**
 * Правки папки в окне «папки и правки»: открытые вопросы (по правилу TAG) — всегда и первыми, хоть они старше пяти
 * последних заявок («текст + время» дают по две заявки — вопрос уезжал за срез и оставался без кнопок, 04.11); так же —
 * закрытый вопрос, чей текст, который не влез, так и не лёг (`proposedPending`), пока человек не закрыл его тост
 * (`dismissed`); дальше — последние `SHOWN_FIXES`.
 */
export const visibleFixes = (fixes: StreamFix[], dismissed: ReadonlySet<string> = new Set<string>()): StreamFix[] => {
    const recent = fixes.slice(0, SHOWN_FIXES);
    const older = fixes
        .slice(SHOWN_FIXES)
        .filter(
            (fx) =>
                Boolean(fx.question) &&
                (isQuestionOpen(fx, fixes) || (!dismissed.has(fx.id) && proposedPending(fx, fixes))),
        );
    return [...older, ...recent];
};
